import { z } from 'zod';
import { CURRENCIES, PURCHASE_ORDER_STATUSES } from '../enums.js';
import { optionalText, paginationSchema, patchSchemaOf } from './common.js';
/**
 * Raising an order.
 *
 * Cost is optional per line: MEFOSA orders media before the supplier has
 * quoted a price often enough that requiring it would push people to type a
 * guess, and a guessed cost is worse than a blank one.
 */
export const purchaseOrderLineInputSchema = z.object({
    productId: z.string().uuid(),
    quantityOrdered: z.coerce.number().int().positive('Order at least one'),
    /** Major units in, converted against the ORDER's currency on the way in. */
    unitCostMajor: z.coerce.number().nonnegative().optional(),
    notes: optionalText,
});
export const purchaseOrderInputSchema = z.object({
    supplierId: z.string().uuid(),
    orderDate: z.string().date(),
    /**
     * Nullable, not merely optional. An edit has to be able to REMOVE a date
     * that turned out wrong, and neither other value can say so: '' fails
     * z.string().date(), and JSON.stringify drops an undefined key so the field
     * never reaches the server at all.
     */
    expectedDate: z.string().date().nullable().optional(),
    supplierReference: optionalText,
    /** One currency for the whole order, so no line can hold a bare amount. */
    currency: z.enum(CURRENCIES).default('USD'),
    notes: optionalText,
    lines: z.array(purchaseOrderLineInputSchema).min(1, 'An order needs at least one product'),
});
/**
 * .partial() alone is NOT enough here.
 *
 * The currency field carries .default('USD'), and .partial() wraps it as
 * ZodOptional<ZodDefault<...>> — the inner default STILL fires. So a PATCH
 * that never mentioned currency silently rewrote the order to USD while
 * every line's cost stayed in the old currency's minor units, turning a
 * 9,000,000 LBP order into a 9,000,000 USD one. Re-declared here without a
 * default, so an omitted field stays omitted.
 */
export const purchaseOrderUpdateSchema = patchSchemaOf(purchaseOrderInputSchema).extend({
    currency: z.enum(CURRENCIES).optional(),
});
/**
 * Booking a delivery in.
 *
 * One entry per line that arrived — a delivery need not cover every line, and
 * a line may arrive across several deliveries, which is the whole reason
 * received quantity is derived from batches rather than typed on the line.
 *
 * Lot number and expiry live HERE and not on the order: they are facts about
 * the box that turned up, not about what was asked for. DECISIONS.md §9.
 */
export const receiveLineSchema = z.object({
    lineId: z.string().uuid(),
    quantity: z.coerce.number().int().positive('Receive at least one'),
    batchNumber: optionalText,
    expiryDate: z.string().date().optional(),
    /** Overrides the ordered cost when the supplier billed something else. */
    unitCostMajor: z.coerce.number().nonnegative().optional(),
    notes: optionalText,
});
export const receivePurchaseSchema = z.object({
    receivedDate: z.string().date().optional(),
    /** The supplier's delivery note or invoice number for this drop. */
    reference: optionalText,
    lines: z.array(receiveLineSchema).min(1, 'Record at least one line'),
});
export const cancelPurchaseSchema = z.object({
    // Required by the route even though the column is nullable: an order
    // cancelled with no reason is one nobody can explain to the supplier.
    reason: z.string().trim().min(3, 'Say why it was cancelled'),
});
export const purchaseQuerySchema = paginationSchema.extend({
    status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
    supplierId: z.string().uuid().optional(),
    /** Orders still owing something, which is the list somebody chases. */
    outstanding: z.enum(['true', 'false']).optional(),
});
//# sourceMappingURL=purchasing.js.map